import { useThemeMode, type ThemeMode } from '@/hooks/use-theme-mode.hook';
import {
  Check,
  DarkMode,
  LightMode,
  SettingsBrightness,
  type SvgIconComponent
} from '@mui/icons-material';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import {
  useMemo,
  useState,
  type FunctionComponent,
  type MouseEvent
} from 'react';
import { useTranslation } from 'react-i18next';

export const MODE_OPTIONS: ReadonlyArray<{
  value: ThemeMode;
  label: string;
  Icon: SvgIconComponent;
}> = [
  { value: 'system', label: 'System', Icon: SettingsBrightness },
  { value: 'light', label: 'Light', Icon: LightMode },
  { value: 'dark', label: 'Dark', Icon: DarkMode }
];

// Trigger icon reflects the *resolved* scheme. Before hydration
// `resolvedMode` is undefined — fall back to the neutral System icon so
// SSR and the first client render match.
const RESOLVED_MODE_ICON: Record<'light' | 'dark', SvgIconComponent> = {
  light: LightMode,
  dark: DarkMode
};

export const ThemeToggle: FunctionComponent = () => {
  const { t } = useTranslation();
  const themeLabels = useMemo(
    () => ({
      system: t('common:theme.system'),
      light: t('common:theme.light'),
      dark: t('common:theme.dark')
    }),
    [t]
  );
  const { mode, resolvedMode, setMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const TriggerIcon = resolvedMode
    ? RESOLVED_MODE_ICON[resolvedMode]
    : SettingsBrightness;

  const handleOpen = (e: MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (value: ThemeMode) => {
    setMode(value);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        id="theme-button"
        aria-label={t('common:theme.changeThemeAriaLabel')}
        aria-haspopup="menu"
        aria-controls={open ? 'theme-menu' : undefined}
        aria-expanded={open || undefined}
        onClick={handleOpen}
        sx={{ color: 'text.secondary' }}
      >
        <TriggerIcon fontSize="small" />
      </IconButton>
      <Menu
        id="theme-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: { sx: { minWidth: 160 } },
          list: { 'aria-labelledby': 'theme-button' }
        }}
      >
        {MODE_OPTIONS.map(({ value, Icon }) => {
          const selected = mode === value;
          return (
            <MenuItem
              key={value}
              onClick={() => handleSelect(value)}
              selected={selected}
              role="menuitemradio"
              aria-checked={selected}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{themeLabels[value]}</ListItemText>
              {selected ? <Check fontSize="small" /> : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
